import {
  BasketViewModel,
  BasketTourViewModel,
  BasketHotelViewModel,
  BasketVariationPromotionViewModel,
  LoyaltyType,
} from '@api/v3';

import { SelectablePromotion } from './Hopi/types';

const isHotel = (
  item: BasketHotelViewModel | BasketTourViewModel
): item is BasketHotelViewModel => {
  return (item as BasketHotelViewModel).pricing !== undefined;
};

const getItemPrice = (item: BasketHotelViewModel | BasketTourViewModel) => {
  return isHotel(item) ? item.pricing.discounted : item.price;
};

export const promotionReducer =
  <
    R extends BasketVariationPromotionViewModel = BasketVariationPromotionViewModel
  >(
    loyaltyType: LoyaltyType,
    selected: boolean,
    merger: (p: BasketVariationPromotionViewModel) => R = (p) =>
      ({
        ...p,
        selected,
      } as unknown as R)
  ) =>
  (acc: R[], p: BasketVariationPromotionViewModel) => {
    if (p.loyaltyType !== loyaltyType) return acc;
    return [...acc, merger(p)] as R[];
  };

const getAvailableLoyaltiesFor = (
  items: (BasketHotelViewModel | BasketTourViewModel)[],
  type: 'Hotel' | 'Tour',
  loyaltyType: LoyaltyType
) => {
  let hasSelectedPromotion = false;
  const promotions = items.flatMap((item) =>
    item.variations.reduce((variationsAccumulator, v) => {
      if (v.loyaltyType !== loyaltyType || v.price !== getItemPrice(item))
        return variationsAccumulator;
      if (v.selected) hasSelectedPromotion = true;
      const selectablePromotionReducer = promotionReducer<SelectablePromotion>(
        loyaltyType,
        v.selected,
        (p) => ({
          ...p,
          selected: v.selected,
          item,
          type,
          variation: v,
          entityId: item.itemId ?? '',
        })
      );
      return [
        ...variationsAccumulator,
        ...v.promotions.reduce(
          selectablePromotionReducer,
          [] as Array<SelectablePromotion>
        ),
      ];
    }, [] as Array<SelectablePromotion>)
  );
  return {
    promotions,
    hasSelectedPromotion,
    selectedPromotion: promotions.find((p) => p.variation.selected),
  };
};

export const getPromotions = (
  basket: Pick<BasketViewModel, 'hotels' | 'tours'>,
  loyaltyType: LoyaltyType
) => {
  const { hotels = [], tours = [] } = basket;
  const hotelsPromotions = getAvailableLoyaltiesFor(
    hotels,
    'Hotel',
    loyaltyType
  );
  const toursPromotions = getAvailableLoyaltiesFor(tours, 'Tour', loyaltyType);
  return {
    hasLoyalty: !!(
      hotelsPromotions.promotions.length || toursPromotions.promotions.length
    ),
    hotelsIds: hotelsPromotions?.promotions?.map((p) => p.entityId),
    toursIds: toursPromotions?.promotions?.map((p) => p.entityId),
    type: loyaltyType,
    isLoyaltySelectedForHotels: hotelsPromotions.hasSelectedPromotion,
    isLoyaltySelectedForTours: toursPromotions.hasSelectedPromotion,
    hotelsPromotions,
    toursPromotions,
  };
};

export const checkBankCardPromotion = (
  basket: Pick<BasketViewModel, 'hotels'>
): { itemId: string } | null => {
  const { hotels = [] } = basket;

  const hotelWithBankPromotion = hotels.find((hotel) =>
    hotel.variations?.some((variation) =>
      variation.promotions?.some(
        (promotion) => promotion.promotionType === 'BankPosDiscountPromotion'
      )
    )
  );

  if (hotelWithBankPromotion?.itemId) {
    return { itemId: hotelWithBankPromotion.itemId };
  }
  return null;
};

export const findSelectedBankPromotion = (hotel) => {
  const selectedVariation = hotel.variations?.find(
    (variation) => variation.selected === true
  );

  if (!selectedVariation) {
    return null;
  }

  const bankPromotion = selectedVariation.promotions?.find(
    (promotion) => promotion.promotionType === 'BankPosDiscountPromotion'
  );

  if (bankPromotion) {
    return {
      variation: selectedVariation,
      promotion: bankPromotion,
    };
  }

  return null;
};
